<template>
    <!-- All AI workspace sections are equal peers on one row. Icon-only with
         tooltips keeps every tab the same width inside the narrow rail; a
         coloured active state would read as an error, so neutral greys only. -->
    <div class="flex items-center gap-1">
        <inertia-link
            v-for="tab in tabs"
            :key="tab.id"
            v-tooltip="{
                content: tab.name,
                delay: { show: 400, hide: 100 },
            }"
            :href="tab.href"
            preserve-scroll
            :aria-current="tab.active ? 'page' : undefined"
            :aria-label="tab.name"
            class="flex flex-1 items-center justify-center rounded-lg px-3 py-2 transition"
            :class="
                tab.active
                    ? 'bg-gray-200 text-gray-800'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
            "
        >
            <component :is="tab.icon" class="h-5 w-5 shrink-0" />
        </inertia-link>
    </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import type { Component } from "vue";
import { usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import {
    ChatBubbleLeftRightIcon,
    DocumentTextIcon,
    TagIcon,
    Squares2X2Icon,
    SparklesIcon,
} from "@heroicons/vue/24/outline";
import { useTranslations } from "@/composables/useTranslations";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";

interface IAiTab {
    id: string;
    name: string;
    href: string;
    icon: Component;
    active: boolean;
}

const { t } = useTranslations();

const url = computed(() => usePage().url);
const globalProps = computed(() => usePage<IRhizomPageProps>().props);

// The chat is active everywhere under /ai-chat except its (flagged) dashboard.
const chatActive = computed(
    () =>
        url.value.startsWith("/ai-chat") &&
        !url.value.startsWith("/ai-chat/dashboard"),
);

const tabs = computed((): IAiTab[] => {
    const list: IAiTab[] = [
        {
            id: "ai-chat",
            name: t("vue.ai_chat"),
            href: route("ai-chat.index"),
            icon: ChatBubbleLeftRightIcon,
            active: chatActive.value,
        },
    ];

    if (globalProps.value.inAppAiChatDashboardActive) {
        list.push({
            id: "ai-dashboard",
            name: t("vue.ai_dashboard"),
            href: route("ai-chat.dashboard.index"),
            icon: Squares2X2Icon,
            active: url.value.startsWith("/ai-chat/dashboard"),
        });
    }

    list.push({
        id: "ai-content",
        name: t("vue.ai_content"),
        href: route("ai-content.index"),
        icon: DocumentTextIcon,
        active: url.value.startsWith("/ai-content"),
    });

    if (globalProps.value.inAppAiChatTeamSkillsActive) {
        list.push({
            id: "ai-skills",
            name: t("vue.ai_skills"),
            href: route("ai-skills.index"),
            icon: SparklesIcon,
            active: url.value.startsWith("/ai-skills"),
        });
    }

    list.push({
        id: "ai-tags",
        name: t("vue.ai_tags"),
        href: route("ai-tags.index"),
        icon: TagIcon,
        active: url.value.startsWith("/ai-tags"),
    });

    return list;
});
</script>
